// Knowledge page strings: long-form company knowledge pages (Pages tab, page and suggestion
// canvases, repository import, the suggested-update chat card), the Knowledge overview and the
// Knowledge empty state on the canvas (UX WS-14).
//
// Namespace `knowledge-pages`; owner DES2 for the copy, the owners of ui/workspace/knowledge-pages,
// ui/workspace/knowledge-overview.tsx and ui/workspace/canvas.tsx for the code. Placeholders are
// `{name}`, filled with `formatCopy`. Count values are plural forms for `formatCount`
// ("one:…|other:…", never with the number). Server error codes, page titles, paths and other
// content from data are never translated here.
import type { NamespaceCatalog } from '@/i18n/ns';
import bg from '../knowledge-pages/bg';
import cs from '../knowledge-pages/cs';
import da from '../knowledge-pages/da';
import de from '../knowledge-pages/de';
import el from '../knowledge-pages/el';
import es from '../knowledge-pages/es';
import et from '../knowledge-pages/et';
import fi from '../knowledge-pages/fi';
import fr from '../knowledge-pages/fr';
import ga from '../knowledge-pages/ga';
import hr from '../knowledge-pages/hr';
import hu from '../knowledge-pages/hu';
import it from '../knowledge-pages/it';
import lt from '../knowledge-pages/lt';
import lv from '../knowledge-pages/lv';
import mt from '../knowledge-pages/mt';
import nl from '../knowledge-pages/nl';
import pl from '../knowledge-pages/pl';
import pt from '../knowledge-pages/pt';
import ro from '../knowledge-pages/ro';
import sk from '../knowledge-pages/sk';
import sl from '../knowledge-pages/sl';
import sv from '../knowledge-pages/sv';

const en = {
  // Shared across the surface.
  knowledgePage: 'Knowledge page',
  knowledgePages: 'Knowledge pages',
  featureOffTitle: 'Knowledge pages aren’t enabled in this workspace.',
  featureOffBody: 'Company context and sources are still in Knowledge.',
  loadingPage: 'Loading page…',
  loadingSuggestion: 'Loading suggestion…',
  cancel: 'Cancel',
  tryAgain: 'Try again',
  everyoneIn: 'Everyone in {company}',
  yourCompany: 'your company',
  revision: 'Revision {n}',
  openRevision: 'Open revision {n}',
  pageCount: 'one:page|other:pages',
  topicCount: 'one:topic|other:topics',
  sectionCount: 'one:section|other:sections',
  lineCount: 'one:line|other:lines',
  documentCount: 'one:document|other:documents',
  changeCount: 'one:change|other:changes',
  newPageCount: 'one:new page|other:new pages',
  changedPageCount: 'one:changed page|other:changed pages',
  otherSectionCount: 'one:other section|other:other sections',
  characterCount: 'one:character|other:characters',
  diffStats: '{sections} changed · {added} added · {removed} removed',
  diffMatchesBase: 'The proposed text matches the base revision.',
  diffIntroduction: 'Introduction',
  diffOtherUnchanged: '{sections} unchanged.',

  // format.ts: dates, attribution and status labels.
  timeJustNow: 'just now',
  originImportedFrom: 'Imported from {path}',
  originImportedFromCommit: 'Imported from {path} @ {commit}',
  authorityApprovedInPoneva: 'Approved in Poneva',
  authorityImported: 'Imported',
  authorityInRepository: '{canonicality} in repository',
  authorityVerified: 'verified {date}',
  authorRepositoryImport: 'Repository import',
  authorSuggestedInChat: 'Suggested in chat',
  authorSuggestedInChatWith: 'Suggested in chat with {name}',
  authorSuggestedBy: 'Suggested by {name}',
  authorSuggestedByApp: 'Suggested by a connected app',
  authorSuggestedByColleague: 'Suggested by a colleague',
  authorSuggestedByYou: 'Suggested by you',
  authorSuggestedOnBehalf: 'Suggested by {app} on behalf of {name}',
  authorSuggestedByAppOnBehalf: 'Suggested by a connected app on behalf of {name}',
  authorSuggestedAndApproved: 'Suggested and approved by {name}',
  approverApprovedBy: 'approved by {name}',
  approverAppliedBy: 'applied by {name}',
  kindCreatePage: 'New page',
  kindUpdatePage: 'Proposed update',
  kindArchivePage: 'Archive',
  statusConflict: 'Conflict · page changed',
  statusPending: 'Pending review',
  statusApproved: 'Approved',
  statusRejected: 'Dismissed',
  statusWithdrawn: 'Withdrawn',
  statusSuperseded: 'Superseded',

  // api.ts: why a pages request failed (by server error code).
  errRevisionConflict: 'This page changed while you were reviewing it.',
  errProposalChanged:
    'This suggestion changed while you were reviewing it. Review the latest version before deciding.',
  errPublishRequired: 'Only company publishers can approve knowledge changes or import pages.',
  errProposalLimit:
    'Your company already has 200 suggestions waiting for review. Review some before suggesting more.',
  errAuthorLimit:
    'You already have 50 suggestions waiting for review. Wait until some are decided, or withdraw some, before suggesting more.',
  errImportProposalLimit:
    'This import would add more changed pages for review than fit in your company’s review queue (200 at most). Decide some suggestions in For review, then apply again.',
  errImportChanged:
    'This import isn’t the one you reviewed. Open it again and review it before applying.',
  errImportItemNotFound: 'This document is no longer part of the import.',
  errIdempotencyConflict:
    'This suggestion was already sent with different content. Reload and try again.',
  errImportHashMismatch:
    'A file in this snapshot doesn’t match its recorded checksum. Create a new snapshot and try again.',
  errImportAlreadyApplied: 'This import has already been applied.',
  errImportDiscarded: 'This import was discarded. Choose the snapshot file again.',
  errImportDuplicateItem: 'This snapshot lists the same document twice. Create the snapshot again.',
  errInvalidImport:
    'Poneva couldn’t accept this snapshot. Create a new one with the snapshot script and try again.',
  errInvalidInput: 'Poneva couldn’t accept this input. Check the fields and try again.',
  errImportLimit:
    'Too many imports are staged but not applied. Apply or discard some before staging another.',
  errPageTooLarge: 'A page is larger than 256 KB. Split it into smaller pages.',
  errTooManySections: 'A page has more than 2,000 headings. Split it into smaller pages.',
  errPageLimit: 'Your company has reached the limit of 2,000 knowledge pages.',
  errCollectionLimit: 'Your company has reached the limit of 100 topics.',
  errProposalNotPending: 'This suggestion has already been decided. The status shown is current.',
  errRequesterRemoved:
    'The person who suggested this is no longer in this company, so it can’t be approved.',
  errRevisionAlreadyCurrent: 'That revision is already the current one.',
  errPageArchived:
    'This page was archived while you were reviewing it. Review the suggestion again before deciding.',
  errAlreadyArchived: 'This page is already archived.',
  errProposalForbidden: 'Only the person who suggested this can withdraw it.',
  errUnavailable: 'Knowledge pages are unavailable right now.',
  errTimeout: 'Poneva took too long to answer.',
  errItemTooLarge: '{path} is larger than 256 KB. Split it into smaller pages.',
  errItemTooManySections: '{path} has more than 2,000 headings. Split it into smaller pages.',
  errItemHashMismatch:
    '{path} doesn’t match its recorded checksum. Create a new snapshot and try again.',
  errNoLongerAvailable: 'This item is no longer available to you.',
  errTooLargeToSend: 'This file is too large to send.',

  // chat-card.tsx: the suggested-update card in the chat transcript.
  cardChecking: 'Checking status…',
  cardNotAvailable: 'Not available',
  cardStatusUnavailable: 'Status unavailable',
  cardUnshowable: 'This suggested knowledge update couldn’t be shown.',
  cardEyebrow: 'Suggested knowledge update',
  cardLabel: 'Suggested knowledge update · {title}',
  cardMissing: 'This suggestion is no longer available to you.',
  cardUnknown: 'We couldn’t check where this suggestion stands. Open the review to see its status.',
  cardCheckAgain: 'Check again',
  cardReview: 'Review update',

  // overview.tsx: the status line and pinned topics on the Knowledge overview.
  statusLineApproved: 'Approved understanding',
  readyCount: 'one:ready for search|other:ready for search',
  preparingCount: 'one:preparing|other:preparing',
  attentionCount: 'one:need attention|other:need attention',
  topicsExplore: 'Explore a topic',
  topicsAllPages: 'All pages',
  topicsOpenPages: 'Open Pages',
  topicsEmpty:
    'No approved pages yet. Pages hold long-form company understanding with its full revision history.',

  // reviews.tsx: suggested page updates in Knowledge → For review.
  reviewsTitle: 'Suggested page updates',
  reviewsTitleMine: 'Your suggested page updates',
  reviewsIntro:
    'Each suggestion is a complete candidate revision with its reason and evidence. Nothing changes until you approve that exact revision.',
  reviewsIntroMine:
    'Your suggestions wait here until a company publisher reviews them. The pages stay as they are until then.',

  // use-knowledge-pages.ts
  loadPagesFailed: 'We couldn’t load knowledge pages.',
  loadProposalsFailed: 'We couldn’t load suggested page updates.',
  loadMoreFailed: 'We couldn’t load more pages.',

  // pages-tab.tsx: the Pages tab.
  otherPages: 'Other pages',
  loadTopicFailed: 'We couldn’t load this topic.',
  revisionShort: 'r{n}',
  updatedOn: 'Updated {date}',
  emptyTitle: 'No knowledge pages yet.',
  emptyBody:
    'Pages hold your company’s approved, long-form understanding — direction, decisions and ways of working — with every revision kept. Import from a repository snapshot or start a page in chat; a publisher approves each page before it counts as company knowledge.',
  importFromSnapshot: 'Import from a repository snapshot',
  startInChat: 'Start a page in chat',
  startInChatPrompt:
    'Help me start a knowledge page for our company. Ask what it should cover and do not invent content. It stays a suggestion until a company publisher approves it.',
  loadingPages: 'Loading knowledge pages…',
  filterLabel: 'Filter pages by title',
  filterPlaceholder: 'Filter pages',
  importButton: 'Import',
  showingTopic: 'Showing {topic}',
  showAllTopics: 'Show all topics',
  loadingTopic: 'Loading this topic…',
  noMatch: 'No pages match “{filter}”.',
  topicNoPages: 'No approved pages in this topic yet.',
  loading: 'Loading…',
  loadMore: 'Load more pages',

  // page-canvas.tsx: one page, its history and the edit form.
  metaApproved: 'approved {date}',
  metaRepositoryAuthoritative: 'repository remains authoritative',
  loadPageFailed: 'We couldn’t load this page.',
  loadHistoryFailed: 'We couldn’t load the revision history.',
  outlineLabel: 'Page sections',
  outlineToggle: 'On this page',
  loadingHistory: 'Loading revision history…',
  historyLabel: 'Revision history',
  historyIntro:
    'Every approved revision is kept. Restoring an earlier one creates a suggestion for review; it never erases later revisions.',
  historyCurrent: 'Current',
  historyViewing: 'Viewing',
  historyView: 'View',
  historyViewAria: 'View revision {n}',
  restoreAria: 'Restore as a suggestion: revision {n}',
  restoreCreating: 'Creating suggestion…',
  restore: 'Restore as a suggestion',
  restoreUnconfirmed:
    'We couldn’t confirm that the suggestion was created. Check For review before trying again.',
  editTooLarge:
    'This page is larger than 256 KB. Split it into smaller pages before suggesting it.',
  editConflict:
    'This page changed while you were editing. Copy your text, open the latest revision and suggest the edit again.',
  editUnconfirmed:
    'We couldn’t confirm that your suggestion was sent. Check For review before sending it again.',
  editHeading: 'Suggest an edit',
  editIntro:
    'You’re editing revision {n}. A company publisher reviews the exact change before it becomes revision {next}; until then the page stays as it is.',
  editOutdated:
    'A newer revision was approved while you were editing. Copy your text, then start again from the latest revision so nothing it changed is lost.',
  editContentLabel: 'Page content (Markdown)',
  editReasonLabel: 'Why this change?',
  editReasonPlaceholder: 'What changed, and where it comes from.',
  editSending: 'Sending…',
  editSend: 'Send for review',
  editDiscardConfirm: 'Discard this edit? Nothing you changed has been sent.',
  editNoChanges: 'No changes yet.',
  editNeedsContent: 'A page needs some content.',
  exportFailed: 'We couldn’t export this page. Try again.',
  askPagePrompt: 'Question about the knowledge page “{title}” (revision {n}, {link}):',
  askPageSectionPrompt:
    'Question about the knowledge page “{title}” § {section} (revision {n}, {link}):',
  pageMissingTitle: 'This page isn’t available.',
  pageMissingBody:
    'It may have been archived, or you may no longer have access to this company’s knowledge.',
  pageArchived: 'Archived',
  viewingRevisionCurrent: 'You’re viewing revision {n}; the current revision is {current}.',
  viewingRevisionOld: 'You’re viewing revision {n}. It isn’t the current revision.',
  openCurrent: 'Open current',
  archivedBanner: 'This page is archived. It no longer informs answers; its history is kept.',
  pageViewsLabel: 'Page views',
  tabRead: 'Read',
  tabHistory: 'History',
  unknownSection: 'The linked section isn’t part of this revision. Showing the whole page.',
  askAboutPage: 'Ask about this page',
  exporting: 'Exporting…',
  exportMarkdown: 'Export Markdown',

  // proposal-canvas.tsx: reviewing one suggestion.
  loadSuggestionFailed: 'We couldn’t load this suggestion.',
  evidenceSource: 'Source',
  evidenceFact: 'Company context',
  evidenceResult: 'Saved work',
  evidenceUrl: 'Web page',
  evidenceDecision: 'Decision',
  evidenceReference: 'Reference',
  evidenceOpenPage: 'Open knowledge page',
  evidenceOpenSource: 'Open source',
  evidenceOpenFact: 'Open company context',
  evidenceOpenResult: 'Open saved work',
  evidenceOpenUrl: 'Open web page',
  evidenceOpenDecision: 'Open decision',
  evidenceOpenReference: 'Open reference',
  evidenceDecisionNote: 'A recorded decision. There is nothing in Poneva to open.',
  evidenceFactGone: 'This company context is no longer saved.',
  evidencePageNoRevision: 'This page has no approved revision to open.',
  evidencePrivateExcerpt: 'Private evidence — only the excerpt above was shared.',
  evidencePrivate: 'Private evidence — its content was not shared with you.',
  evidenceHeading: 'Evidence',
  evidenceNone: 'No evidence was attached. Weigh the reason above on its own.',
  changeAdded: 'Added',
  changeRemoved: 'Removed',
  changeChanged: 'Changed',
  changeUnchanged: 'Unchanged',
  moreLineCount: 'one:more line|other:more lines',
  moreUnchangedLineCount: 'one:more unchanged line|other:more unchanged lines',
  unchangedLineCount: 'one:unchanged line|other:unchanged lines',
  skipAdded: '{lines} — shown in the complete revision',
  skipRemoved: '{lines} removed',
  cutUnchanged: '{lines} not shown',
  cutChanged: '{lines} not shown, {changes}. Check them in the complete revision.',
  cutAddedRemoved: '{added} added and {removed} removed',
  cutAdded: '{added} added',
  cutRemoved: '{removed} removed',
  lineAddedSr: 'Added:',
  lineRemovedSr: 'Removed:',
  noLinesAdded: 'A new section. Its text is in the complete revision below.',
  noLinesRemoved: 'This section would be removed from the page.',
  noLinesChanged: 'This section changes. Its text is in the complete revision below.',
  diffHeading: 'Changes',
  outcomeArchived: 'Archived.',
  outcomeArchivedBody:
    '{title} no longer informs answers and has left the page list. Its revisions, this suggestion and the decision stay in the page history.',
  openArchivedPage: 'Open archived page',
  outcomeApproved: 'Approved.',
  outcomeApprovedRevision: 'Revision {n} is now the approved version of {title}.',
  outcomeApprovedPlain: 'This suggestion was approved.',
  outcomeRestored: 'The page is back in the page list and informs answers again.',
  outcomeKept: 'The suggestion, the decision and every earlier revision stay in the page history.',
  openPage: 'Open page',
  outcomeDismissed: 'Dismissed.',
  outcomeWithdrawn: 'Withdrawn.',
  outcomeSuperseded: 'Superseded.',
  outcomeSupersededBody:
    'A newer revision, suggestion or repository import made this suggestion obsolete. It changed nothing on its own.',
  outcomeUnchanged: 'The page stays as it is.',
  approveNewPage: 'Approve new page',
  approveArchive: 'Approve archive',
  approveRestoreRevision: 'Restore and approve revision {n}',
  approveRevision: 'Approve revision {n}',
  approveArchiveNote: 'Approving archives this page for everyone in {company}.',
  approvePublishNote: 'Approving publishes this revision to everyone in {company}.',
  approvePublishEvidenceNote:
    'Approving publishes this revision and the evidence excerpts shown above to everyone in {company}.',
  archivedTitle: 'This page is archived.',
  alreadyArchivedBody: 'Approving would change nothing, so this suggestion can only be dismissed.',
  archivedRestoreBody:
    'Approving restores it: the page returns to the page list, search and answers with this revision.',
  staleRevisionAction: 'Compare with the latest version',
  staleProposalTitle: 'This suggestion changed while you were reviewing it.',
  staleProposalAction: 'Review the latest version',
  staleArchivedTitle:
    'This page was archived while you were reviewing it. Approving this suggestion would bring it back.',
  staleArchivedAction: 'Review again',
  suggestionMissingTitle: 'This suggestion isn’t available.',
  suggestionMissingBody:
    'It may have been withdrawn, or it belongs to someone else and hasn’t been sent for company review.',
  decideUnconfirmed:
    'We couldn’t confirm your decision. The status shown is the latest from Poneva; check it before trying again.',
  decideFailed: 'We couldn’t save your decision.',
  discussPrompt: 'Help me refine the suggested knowledge update to “{title}” ({link}).',
  conflictBody:
    'This suggestion was written against revision {base}; the current revision is {current}. Approving it would overwrite that change, so it can’t be approved as it stands.',
  conflictBodyPlain:
    'This suggestion was written against an earlier revision, so it can’t be approved as it stands.',
  openLatest: 'Open the latest version',
  reworkInChat: 'Rework in chat',
  factReason: 'Reason',
  factNoReason: 'No reason was given.',
  factAudience: 'Audience',
  factRevision: 'Revision',
  revisionNew: 'A new page, revision 1',
  revisionArchiveAfter: 'Archive after revision {n}',
  revisionWrittenAgainst: 'Written against revision {base}; the current revision is {current}',
  revisionCurrentToProposed: 'Current revision {base} → proposed revision {next}',
  revisionProposed: 'Proposed revision {next}',
  proposedPage: 'Proposed page',
  archivingHeading: 'What archiving does',
  archivingBody:
    'The page stops informing answers and leaves the page list. Its revisions and this decision are kept.',
  noComparison: 'A section comparison isn’t available for this suggestion.',
  showComplete: 'Show complete revision',
  approving: 'Approving…',
  dismissing: 'Dismissing…',
  dismiss: 'Dismiss',
  withdrawing: 'Withdrawing…',
  withdraw: 'Withdraw',
  editInChat: 'Edit in chat',
  waitingForPublisher: 'Waiting for a company publisher to review.',

  // import-review.tsx: importing pages from a repository snapshot.
  snapshotTooLarge: 'This snapshot is larger than 8 MB. Create it with fewer documents.',
  snapshotNotSnapshot:
    'This file isn’t a knowledge snapshot. Choose the JSON file the snapshot script wrote.',
  snapshotEmpty: 'This snapshot contains no documents.',
  readDocumentFailed: 'We couldn’t read this document. Try again.',
  readingDocument: 'Reading the document…',
  hideText: 'Hide text',
  readText: 'Read text',
  textOf: 'Text of {title}',
  gitSnapshot: 'Git snapshot {commit}',
  repositorySnapshot: 'Repository snapshot',
  importNewHeading: '{pages} · approved when you apply',
  importNewNote:
    'Each is published as an approved page for everyone in {company}, with no further review. Read each one before you apply.',
  importChangedHeading: '{pages} · sent for review',
  importChangedNote:
    'Each becomes a suggested update. Its page changes only when a publisher approves it in For review, which shows what changed.',
  unchangedDocumentCount: 'one:unchanged document|other:unchanged documents',
  unchangedNote: 'These already match their approved pages. Nothing is written for them.',
  stagedHeading: 'Staged but not applied',
  excludedSummary: '{documents} left out of this snapshot',
  countsLine: '{newPages} · {changedPages} for review · {unchanged} unchanged',
  importAlreadyDiscarded: 'This import has already been discarded.',
  confirmLabel: 'Confirm import',
  unchangedKeepCount:
    'one:unchanged document keeps its page|other:unchanged documents keep their pages',
  confirmBody:
    'Apply this import for everyone in {company}? {newPages} will be approved as revision 1, {changedPages} will wait for review, and {unchanged}. Earlier import suggestions for these pages are withdrawn.',
  applying: 'Applying…',
  applyImport: 'Apply import',
  chooseAnother: 'Choose another file',
  allCurrent: 'Everything in this snapshot is already current.',
  importApplied: 'Import applied.',
  appliedSummary: '{pages} created · {changes} waiting for review · {unchanged} unchanged.',
  appliedIndexing:
    'New pages become searchable in chat once indexing finishes. Source preparation shows progress.',
  openPages: 'Open pages',
  reviewChanges: 'Review {changes}',
  checkIndexing: 'Check indexing progress',
  sendSnapshotFailed: 'We couldn’t send this snapshot. Try again.',
  openStagedFailed: 'We couldn’t open this staged import.',
  applyUnconfirmed:
    'We couldn’t confirm whether the import was applied. Check Pages before trying again.',
  applyFailed: 'We couldn’t apply this import.',
  importHeading: 'Import pages from the repository',
  backToPages: 'Back to pages',
  importLead:
    'Create a snapshot with {command}, then choose the file. You’ll see every document and what it would change before anything is saved.',
  snapshotFileLabel: 'Knowledge snapshot file',
  readingSnapshot: 'Reading snapshot…',
  openingStaged: 'Opening staged import…',
  chooseSnapshot: 'Choose snapshot file',
  factOrigin: 'Origin',
  factContents: 'Contents',
  importOnlyListed: 'Only the documents listed here are imported.',
  importRepositoryAuthoritative:
    'The repository stays authoritative; changed documents become suggestions for review.',

  // knowledge-overview.tsx: the Knowledge overview card and context sections.
  overviewReviewChanges: 'Review changes',
  overviewConnectAgents: 'Connect external agents',
  overviewEyebrow: 'A shared understanding',
  overviewTitle: '{company}, in context.',
  overviewLeadFallback:
    'Your company’s knowledge starts with the context you share. Build an understanding of your business, together.',
  overviewBody:
    'Company context and source material give Poneva a starting point — not everything there is to know about {company}. Keep building this understanding as your business evolves.',
  overviewAskPrompt: 'What do you know about our company, and what would be useful to add?',
  overviewAsk: 'Ask about {company}',
  overviewAddSources: 'Add sources',
  contextEyebrow: 'What gives answers context',
  contextTitle: 'The business behind the question.',
  contextBody:
    'Website sources explain the public business. Company notes add your terminology, priorities and the way you work.',
  contextPrompt:
    'Help me clarify the company priorities and working preferences we should remember.',
  contextAction: 'Build our understanding',
  learnEyebrow: 'Room to learn',
  learnTitle: 'Start with what matters next.',
  learnBody:
    'Bring in source material or clarify how your company works. Poneva can suggest lasting changes for you to review.',
  learnPrompt:
    'Based on our existing company knowledge, what important information is missing for the work we want to do?',
  learnAction: 'Discuss what would be useful',

  // canvas.tsx: the Knowledge canvas when there is no company context or source yet.
  canvasEmptyTitle: 'A little context goes a long way.',
  canvasEmptyBody:
    'Your company profile is the starting point. Add source material and reviewed notes to help Poneva understand your work.',

  // knowledge.tsx: the Knowledge page, its tabs, suggested updates, facts and the Sources tab.
  pageEyebrow: '{company} workspace',
  pageTitle: 'Company knowledge',
  pageDescription: 'The sources and understanding behind Poneva’s work.',
  tabOverview: 'Overview',
  tabPages: 'Pages',
  tabSources: 'Sources',
  tabReview: 'For review',
  proposalApproved: 'Approved company context',
  proposalDismissed: 'Dismissed suggestion',
  proposalRemoval: 'Proposed removal',
  proposalCorrection: 'Proposed correction',
  proposalContext: 'Proposed company context',
  proposalCurrent: 'Current understanding',
  proposalFactGone:
    'The saved fact is no longer available. Refresh company knowledge before reviewing this removal.',
  proposalReviewFallback: 'Review the proposed company context.',
  proposalNotSaved: 'Not saved yet. Original sources remain unchanged.',
  proposalWaiting: 'Waiting for review · a publisher decides in their Inbox',
  proposalSaving: 'Saving…',
  proposalApproveRemoval: 'Approve removal',
  proposalApproveClarification: 'Approve clarification',
  factLabel: 'Company fact',
  factEyebrow: 'Fact · Everyone at {company}',
  factSuggestCorrection: 'Suggest a correction',
  factAskPrompt: 'Help me understand or clarify this saved company context: “{content}”',
  factAsk: 'Ask about it',
  companyContextHeading: 'Company context',
  companyContextPrompt:
    'I would like to clarify our company knowledge. Help me review what we know and propose an update.',
  companyContextEmpty:
    'No approved notes yet. Start a conversation about your company, or add source material below.',
  refineInChat: 'Refine in chat',
  reviewPanelLabel: 'Knowledge changes for review',
  suggestedUpdatesHeading: 'Suggested updates',
  suggestedUpdatesIntro:
    'Suggestions from your conversations. You decide what becomes part of your company’s lasting knowledge.',
  reviewEmptyTitle: 'Nothing waiting for your review.',
  reviewEmptyBody:
    'When Poneva suggests a change to your company knowledge, you’ll find it here. Lasting changes always need your approval.',
  reviewEmptyPrompt:
    'Help me review our company knowledge and suggest what would be useful to clarify next.',
  inclusionPendingApproval: 'Knowledge inclusion change sent for approval.',
  sourcesFileIncluded: 'File included in knowledge search for its current audience.',
  sourcesAddFailed: 'Could not add this source.',
  sourcesHeading: 'The material behind the answers.',
  sourcesIntro:
    'Poneva searches the full source material, not just the overview. Keep originals, see what is ready, and choose what informs your answers.',
  addSourceHeading: 'Add material from your company',
  addSourceIntro:
    'Uploaded sources stay private in your Library and inform your own answers. Choose Share when you’re ready to make them available to colleagues.',
  uploadSource: 'Upload a source',
  addSourcePrompt:
    'Help me add a company statement as a shared internal source. Ask what I would like to record; do not invent any content. Lasting changes to company understanding still need my approval.',
  addThroughChat: 'Add through chat',
  addingSource: 'Adding…',
  addToKnowledge: 'Add to knowledge',
  uploadFormats: 'TXT, Markdown, CSV and JSON · Up to 5 MB per file',

  // knowledge-source-browser.tsx: the source library on the Sources tab.
  sourceExcluded: 'Excluded from answers',
  sourcePreparationFailed: 'Preparation failed',
  sourcePreparing: 'Preparing for search',
  sourceWaiting: 'Waiting to be prepared',
  sourceReadinessUnknown: 'Readiness not available',
  sourceRetryQueued: 'Preparation queued. Readiness will update when it completes.',
  sourceIncludedNotice: 'Source included. Poneva will prepare it for search.',
  sourceExcludedNotice: 'Excluded from answers. The original source is retained.',
  sourceUpdateFailed: 'This source couldn’t be updated.',
  sourceOriginalMaterial: 'Original source material',
  sourceDiscuss: 'Discuss in chat',
  sourceDiscussPrompt: 'Help me understand or clarify “{title}”.',
  sourceExclude: 'Exclude from answers',
  sourceInclude: 'Include in answers',
  sourcesLoadFailed: 'We couldn’t load your sources.',
  sourceLibraryLabel: 'Company source library',
  sourceListCaption: 'Original sources · included and excluded',
  sourceSearchLabel: 'Search company sources',
  sourceSearchPlaceholder: 'Search your sources…',
  sourcesFinding: 'Finding your sources…',
  sourcePagesLabel: 'Source pages',
  sourceRange: '{from}–{to} of {count}',
  sourceCount: 'one:source|other:sources',
  sourcePrevious: 'Previous',
  sourceNext: 'Next',
  sourceNoMatchTitle: 'No matching sources.',
  sourceNoMatchBody: 'Try a different phrase or source name.',
  sourceEmptyTitle: 'Give your questions a stronger starting point.',
  sourceEmptyBody:
    'Add company documents, product data or working notes. Poneva can use the full source material in conversations and Skills.',

  // knowledge-index-status.tsx: source preparation status (shared with the source library).
  indexCheckFailed: 'We couldn’t check source preparation.',
  indexRetryQueued:
    'Failed knowledge entries queued for preparation. Your source material is unchanged.',
  indexQueueFailed: 'We couldn’t queue preparation. Try again.',
  indexPreparing: 'Preparing new knowledge for your conversations…',
  indexAttention: 'Some knowledge needs attention.',
  queuing: 'Queuing…',
  retryPreparation: 'Retry preparation',

  // knowledge-sources.ts: source kind labels.
  sourceKindStatement: 'Company statement',
  sourceKindWebsite: 'Website',
  sourceKindDocument: 'Document',
  // knowledge-facts.ts and the Knowledge/source canvases in canvas.tsx (DES2)
  factAnswers: 'one:answer|other:answers',
  factUsedThisWeek: 'Used in {answers} this week',
  factLastUsed: 'last used {when}',
  factToday: 'today',
  factYesterday: 'yesterday',
  factImpact: 'Changing this affects a fact used in {answers} this week.',
  factCorrectionAbout: 'About this company fact: “{content}” ({link})',
  factCorrectionOutdated: 'This is out of date:',
  sourceCanvasRecorded: 'Recorded source material',
  sourceCanvasNotIncluded:
    'Not included in company knowledge. The original is retained for reference and can still be used in a specific conversation.',
  sourceCanvasOpenOriginal: 'Open original source',
  sourceCanvasUnavailable: 'Source content is unavailable.',
  sourceCanvasDiscussPrompt: 'Help me understand or clarify “{title}”.',
  sourceCanvasDiscuss: 'Discuss this source',
  sourceCanvasCorrectionNote:
    'A correction becomes proposed company context. The recorded source remains unchanged.',
  contextCanvasEyebrow: 'Available company context',
  contextCanvasTitle: 'Knowledge behind this conversation.',
  contextCanvasBody:
    'Approved understanding and the source material your company has added. Available in chat and Skills.',
  contextCanvasOpen: 'Open Knowledge',
} as const;

export type KnowledgePagesCopyKey = keyof typeof en;
export type KnowledgePagesCopy = Readonly<Record<KnowledgePagesCopyKey, string>>;

// DES2's 23 languages (WS-14), one file each under ui/i18n/knowledge-pages/ (size limit),
// using the Knowledge glossary from #3520 (DES2/handoff/knowledge-glossary-24.json).
const others: Record<string, KnowledgePagesCopy> = {
  bg,
  cs,
  da,
  de,
  el,
  es,
  et,
  fi,
  fr,
  ga,
  hr,
  hu,
  it,
  lt,
  lv,
  mt,
  nl,
  pl,
  pt,
  ro,
  sk,
  sl,
  sv,
};

const languages = [
  'en',
  'bg',
  'cs',
  'da',
  'de',
  'el',
  'es',
  'et',
  'fi',
  'fr',
  'ga',
  'hr',
  'hu',
  'it',
  'lt',
  'lv',
  'mt',
  'nl',
  'pl',
  'pt',
  'ro',
  'sk',
  'sl',
  'sv',
] as const;

const copy = Object.fromEntries(
  languages.map((language) => [language, language === 'en' ? { ...en } : { ...others[language] }]),
) as NamespaceCatalog<KnowledgePagesCopyKey>;

export const knowledgePagesCopy = copy;
export default copy;
